import { useMsal } from "@azure/msal-react";
import {
    ActionIcon,
    Box,
    Button,
    Checkbox,
    Flex,
    Group,
    Modal,
    Paper,
    ScrollArea,
    SimpleGrid,
    Stack,
    TagsInput,
    Text,
    Textarea,
    TextInput,
    Title,
    Tooltip,
    useMantineColorScheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    MantineReactTable,
    type MRT_ColumnDef,
    type MRT_ColumnFilterFnsState,
    type MRT_ColumnFiltersState,
    type MRT_PaginationState,
    type MRT_SortingState,
    useMantineReactTable,
} from "mantine-react-table";
import { useMemo, useRef, useState } from "react";
import {
    ADMIN_BADGE_COLORS,
    DatasourceBadges,
    RoleIdBadge,
    StringBadges,
} from "src/components/adminBadges";
import { copyToClipboard } from "src/utils/copyToClipboard";
import { deleteAPI, getAPI, postAPI } from "src/utils/fetch";
import { useAdmin } from "src/utils/useAdmin";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

export type User = {
    id: string;
    oid?: string;
    org_code: string;
    email: string;
    roles: string[];
    tags: string[];
    country: string;
};

type UserApiResponse = {
    data: Array<User>;
};

interface QueryParams {
    columnFilterFns: MRT_ColumnFilterFnsState;
    columnFilters: MRT_ColumnFiltersState;
    globalFilter: string;
    sorting: MRT_SortingState;
    pagination: MRT_PaginationState;
}

interface MutationParams {
    data: Partial<User>;
}

interface CreateUserParams {
    email: string;
    roles: string[];
    tags: string[];
}

// Read-only field whose value is copied to the clipboard on click
function CopyableField({ label, value }: { label: string; value: string }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        if (!value) return;
        await copyToClipboard(value);
        setCopied(true);
        notifications.show({
            title: "Copied",
            message: `${label} copied to clipboard`,
            color: "green",
        });
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <TextInput
            label={label}
            value={value}
            readOnly
            onClick={handleCopy}
            styles={{ input: { cursor: value ? "pointer" : "default" } }}
            rightSection={
                <Tooltip label={copied ? "Copied!" : "Copy"} position="top" withArrow>
                    <ActionIcon
                        variant="subtle"
                        color={copied ? "green" : "gray"}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCopy();
                        }}
                        disabled={!value}
                        aria-label={`Copy ${label}`}
                    >
                        {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    </ActionIcon>
                </Tooltip>
            }
        />
    );
}

interface Role {
    id: string;
    label?: string;
    description: string;
    datasources: { id: string }[];
}

// Type for the form used in RoleSelectionComponent
interface RoleFormValues {
    roles: string[];
    [key: string]: unknown;
}

interface RoleFormType {
    values: RoleFormValues;
    setFieldValue: (field: string, value: unknown) => void;
}

interface RoleSelectionComponentProps {
    form: RoleFormType;
    roleSearchTerm: string;
    setRoleSearchTerm: (value: string) => void;
}

// Reusable role selection component
export const RoleSelectionComponent = ({
    form,
    roleSearchTerm,
    setRoleSearchTerm,
}: RoleSelectionComponentProps) => {
    const { data: roles = [] } = useRoles();
    const { data: authDatasources = [] } = useAuthDatasources();
    const { colorScheme } = useMantineColorScheme();

    const datasourceDescriptions = useMemo(() => {
        const list = Array.isArray(authDatasources) ? authDatasources : [];
        return Object.fromEntries(list.map((ds) => [ds.id, ds.description || ds.name || ""]));
    }, [authDatasources]);

    const rolesByCategory = useMemo(() => {
        const categories: Record<string, Role[]> = {};

        for (const role of roles) {
            const category = role.label ?? "";
            if (!categories[category]) {
                categories[category] = [];
            }
            categories[category].push(role);
        }

        return Object.fromEntries(
            Object.entries(categories).sort(([a], [b]) => a.localeCompare(b)),
        );
    }, [roles]);

    const filteredRolesByCategory = useMemo(() => {
        const filtered: Record<string, Role[]> = {};

        for (const [category, categoryRoles] of Object.entries(rolesByCategory)) {
            const filteredCategoryRoles = categoryRoles.filter(
                (role) =>
                    role.id.toLowerCase().includes(roleSearchTerm.toLowerCase()) ||
                    role.description?.toLowerCase().includes(roleSearchTerm.toLowerCase()),
            );

            if (filteredCategoryRoles.length > 0) {
                filtered[category] = filteredCategoryRoles;
            }
        }

        return filtered;
    }, [rolesByCategory, roleSearchTerm]);

    return (
        <Box>
            <Text fw={500} mb="xs">
                Roles
            </Text>
            <TextInput
                placeholder="Search roles..."
                mb="sm"
                onChange={(event) => setRoleSearchTerm(event.currentTarget.value)}
                leftSection={<span>🔍</span>}
            />
            <ScrollArea type="auto" style={{ height: 400 }}>
                <Stack gap="xs">
                    {Object.entries(roleSearchTerm ? filteredRolesByCategory : rolesByCategory).map(
                        ([category, categoryRoles]) => {
                            const allSelected = categoryRoles.every((role) =>
                                form.values.roles.includes(role.id),
                            );
                            const someSelected = categoryRoles.some((role) =>
                                form.values.roles.includes(role.id),
                            );
                            return (
                                <Box key={category}>
                                    <Group justify="space-between" mb="xs">
                                        <Text fw={500}>{category}</Text>
                                        <Checkbox
                                            label={`Select all (${categoryRoles.length} ${categoryRoles.length === 1 ? "role" : "roles"})`}
                                            checked={allSelected}
                                            indeterminate={!allSelected && someSelected}
                                            onChange={() => {
                                                if (allSelected) {
                                                    form.setFieldValue(
                                                        "roles",
                                                        form.values.roles.filter(
                                                            (r) =>
                                                                !categoryRoles.some(
                                                                    (cr) => cr.id === r,
                                                                ),
                                                        ),
                                                    );
                                                } else {
                                                    const newRoles = [...form.values.roles];
                                                    for (const role of categoryRoles) {
                                                        if (!newRoles.includes(role.id)) {
                                                            newRoles.push(role.id);
                                                        }
                                                    }
                                                    form.setFieldValue("roles", newRoles);
                                                }
                                            }}
                                            styles={{ label: { cursor: "pointer" } }}
                                        />
                                    </Group>
                                    {categoryRoles.map((role) => (
                                        <Paper
                                            key={role.id}
                                            p="xs"
                                            withBorder
                                            style={(theme) => ({
                                                backgroundColor: form.values.roles.includes(role.id)
                                                    ? colorScheme === "dark"
                                                        ? theme.colors.blue[9]
                                                        : theme.colors.blue[0]
                                                    : undefined,
                                                transition: "background-color 200ms ease",
                                                "&:hover": {
                                                    backgroundColor: form.values.roles.includes(
                                                        role.id,
                                                    )
                                                        ? colorScheme === "dark"
                                                            ? theme.colors.blue[8]
                                                            : theme.colors.blue[1]
                                                        : colorScheme === "dark"
                                                          ? theme.colors.dark[5]
                                                          : theme.colors.gray[0],
                                                },
                                                cursor: "pointer",
                                            })}
                                            onClick={() => {
                                                const isSelected = form.values.roles.includes(
                                                    role.id,
                                                );
                                                form.setFieldValue(
                                                    "roles",
                                                    isSelected
                                                        ? form.values.roles.filter(
                                                              (r: string) => r !== role.id,
                                                          )
                                                        : [...form.values.roles, role.id],
                                                );
                                            }}
                                        >
                                            <Group justify="space-between">
                                                <Box
                                                    style={{
                                                        flexGrow: 1,
                                                        cursor: "pointer",
                                                        userSelect: "none",
                                                    }}
                                                >
                                                    <Checkbox
                                                        label={
                                                            <Box
                                                                style={{ cursor: "pointer" }}
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                }}
                                                            >
                                                                <RoleIdBadge
                                                                    id={role.id}
                                                                    description={role.description}
                                                                />
                                                            </Box>
                                                        }
                                                        checked={form.values.roles.includes(
                                                            role.id,
                                                        )}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            const isSelected =
                                                                e.currentTarget.checked;
                                                            form.setFieldValue(
                                                                "roles",
                                                                isSelected
                                                                    ? [
                                                                          ...form.values.roles,
                                                                          role.id,
                                                                      ]
                                                                    : form.values.roles.filter(
                                                                          (r: string) =>
                                                                              r !== role.id,
                                                                      ),
                                                            );
                                                        }}
                                                        styles={{
                                                            root: {
                                                                width: "100%",
                                                                cursor: "pointer",
                                                            },
                                                            body: {
                                                                width: "100%",
                                                                cursor: "pointer",
                                                            },
                                                            label: {
                                                                width: "100%",
                                                                cursor: "pointer",
                                                            },
                                                            input: { cursor: "pointer" },
                                                        }}
                                                    />
                                                    {role.datasources?.length > 0 && (
                                                        <Group gap={4} ml={30} mt={4}>
                                                            <Text size="xs" c="dimmed">
                                                                Datasources:
                                                            </Text>
                                                            <DatasourceBadges
                                                                items={role.datasources.map(
                                                                    (ds) => ds.id,
                                                                )}
                                                                descriptions={
                                                                    datasourceDescriptions
                                                                }
                                                            />
                                                        </Group>
                                                    )}
                                                </Box>
                                            </Group>
                                        </Paper>
                                    ))}
                                </Box>
                            );
                        },
                    )}
                </Stack>
            </ScrollArea>
            <Group justify="space-between" mt="xs">
                <Text size="sm" color="dimmed">
                    {form.values.roles.length} {form.values.roles.length === 1 ? "role" : "roles"}{" "}
                    selected
                </Text>
                <Group gap="xs">
                    <Button
                        variant="default"
                        size="xs"
                        onClick={() => form.setFieldValue("roles", [])}
                        disabled={form.values.roles.length === 0}
                    >
                        Clear All
                    </Button>
                </Group>
            </Group>
        </Box>
    );
};

// Create a custom hook for user operations
export const useUserOperations = (params: QueryParams) => {
    const isAdmin = useAdmin();
    const { instance } = useMsal();
    const queryClient = useQueryClient();
    const { columnFilterFns, columnFilters, globalFilter, sorting, pagination } = params;

    // GET operation - call hook at top level
    const fetchURL = new URL("/v1/auth/users", BACKEND_URL);
    fetchURL.searchParams.set("start", `${pagination.pageIndex * pagination.pageSize}`);
    fetchURL.searchParams.set("size", `${pagination.pageSize}`);
    fetchURL.searchParams.set("filters", JSON.stringify(columnFilters ?? []));
    fetchURL.searchParams.set("filterModes", JSON.stringify(columnFilterFns ?? {}));
    fetchURL.searchParams.set("globalFilter", globalFilter ?? "");
    fetchURL.searchParams.set("sorting", JSON.stringify(sorting ?? []));

    const usersQuery = useInfiniteQuery<UserApiResponse>({
        queryKey: ["users", pagination],
        queryFn: async () => {
            // Note: enabled: isAdmin already prevents this query from running for non-admins
            const response = await getAPI({ url: fetchURL.href, instance });
            const data = await response?.json();
            return data;
        },
        initialPageParam: 0,
        getNextPageParam: () => undefined,
        placeholderData: (previousData) => previousData,
        enabled: isAdmin,
    });

    // POST operation
    const updateUser = async ({ data }: MutationParams) => {
        const response = await postAPI({
            url: `${BACKEND_URL}/v1/auth/users`,
            payload: data,
            instance,
        });

        if (!response?.ok) {
            throw new Error("Failed to update user");
        }

        // Parse the response to get the updated user
        const updatedUser = (await response?.json()) as User;
        // Update the cache with the new user data - simplified to avoid TS issues
        queryClient.invalidateQueries({ queryKey: ["users"] });

        return updatedUser;
    };

    // Create User operation
    const createUser = async ({ email, roles, tags }: CreateUserParams) => {
        const response = await postAPI({
            url: `${BACKEND_URL}/v1/auth/users/create`,
            payload: { email, roles, tags },
            instance,
        });

        if (!response?.ok) {
            const errorData = await response?.json();
            throw new Error(errorData?.detail || "Failed to create user");
        }

        const createdUser = (await response?.json()) as User;

        // Invalidate the users query to refetch the data
        queryClient.invalidateQueries({ queryKey: ["users"] });

        return createdUser;
    };

    const deleteUser = async ({ id }: { id: string }) => {
        const response = await deleteAPI({
            url: `${BACKEND_URL}/v1/auth/users?user_id=${id}`,
            instance,
        });

        if (!response?.ok) {
            throw new Error("Failed to delete user");
        }

        return response.json();
    };

    return {
        usersQuery,
        updateUser,
        createUser,
        deleteUser,
    };
};

const useRoles = () => {
    const { instance } = useMsal();

    return useQuery<Role[], Error>({
        queryKey: ["roles"],
        queryFn: async () => {
            const response = await getAPI({
                url: `${BACKEND_URL}/v1/auth/roles`,
                instance,
            });
            const jsonData = await response?.json();
            return jsonData;
        },
    });
};

type AuthDatasource = { id: string; description?: string; name?: string };

const useAuthDatasources = () => {
    const { instance } = useMsal();

    return useQuery<AuthDatasource[], Error>({
        queryKey: ["auth-datasources"],
        queryFn: async () => {
            const response = await getAPI({
                url: `${BACKEND_URL}/v1/auth/datasources`,
                instance,
            });
            const jsonData = await response?.json();
            if (Array.isArray(jsonData)) return jsonData;
            if (Array.isArray(jsonData?.data)) return jsonData.data;
            return [];
        },
    });
};

const ManageUsers = () => {
    const { instance } = useMsal();
    const { data: roles, isLoading: isLoadingRoles, refetch: fetchRoles } = useRoles();
    // Use stable empty array reference to prevent unnecessary re-renders
    const stableRoles = useMemo(() => roles ?? [], [roles]);
    const roleDescriptions = useMemo(
        () => Object.fromEntries(stableRoles.map((role) => [role.id, role.description])),
        [stableRoles],
    );

    const columns = useMemo<MRT_ColumnDef<User>[]>(
        () => [
            {
                accessorKey: "id" as const,
                header: "ID",
                accessorFn: (row) => (row ? row.id?.toString() : "") ?? "",
                size: 90,
            },
            {
                accessorKey: "org_code" as const,
                header: "Org Code",
                accessorFn: (row) => (row ? row.org_code?.toString() : "") ?? "",
                size: 140,
            },
            {
                accessorKey: "country" as const,
                header: "Country",
                accessorFn: (row) => (row ? row.country?.toString() : "") ?? "",
                size: 90,
            },
            {
                accessorKey: "tags" as const,
                header: "Tags",
                accessorFn: (row) => (row ? row.tags?.join(", ") : "") ?? "",
                Cell: ({ row }) => (
                    <StringBadges
                        items={row.original.tags ?? []}
                        color={ADMIN_BADGE_COLORS.tag}
                        singleLine
                    />
                ),
            },
            {
                accessorKey: "email" as const,
                header: "Email",
                accessorFn: (row) => (row ? row.email?.toString() : "") ?? "",
                size: 280,
            },
            {
                accessorKey: "roles" as const,
                header: "Roles",
                accessorFn: (row) => (row ? row.roles?.join(", ") : "") ?? "",
                size: 260,
                Cell: ({ row }) => (
                    <StringBadges
                        items={row.original.roles ?? []}
                        color={ADMIN_BADGE_COLORS.role}
                        descriptions={roleDescriptions}
                        singleLine
                    />
                ),
            },
            {
                accessorKey: "oid" as const,
                header: "OID",
                accessorFn: (row) => (row ? row.oid?.toString() : "") ?? "",
            },
        ],
        [roleDescriptions],
    );

    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
    const [columnFilterFns, _setColumnFilterFns] = useState<MRT_ColumnFilterFnsState>(
        Object.fromEntries(columns.map(({ accessorKey }) => [accessorKey, "contains"])),
    ); //default to "contains" for all columns
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "email", desc: false }]);
    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });

    const {
        usersQuery: { data, isLoading, refetch },
        updateUser,
        createUser,
        deleteUser,
    } = useUserOperations({
        columnFilterFns,
        columnFilters,
        globalFilter,
        pagination,
        sorting,
    });

    const fetchedUsers = useMemo(() => {
        if (!data || !data.pages) return [];

        return data.pages.flatMap((page: unknown) => {
            if (!page) return [];
            if (page && Array.isArray(page)) {
                return page as User[];
            }
            return [];
        });
    }, [data]);

    const tableContainerRef = useRef<HTMLDivElement>(null);
    const [isRefetching, setIsRefetching] = useState(false);

    const fetchMoreOnBottomReached = async (containerRefElement?: HTMLDivElement) => {
        if (containerRefElement) {
            const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
            if (
                scrollHeight - scrollTop - clientHeight < 400 &&
                !isRefetching &&
                data?.pages[data.pages.length - 1]?.data?.length === pagination.pageSize
            ) {
                setIsRefetching(true);
                await setPagination((old) => ({
                    ...old,
                    pageIndex: old.pageIndex + 1,
                }));
                setIsRefetching(false);
            }
        }
    };

    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [modalOpened, setModalOpened] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteConfirmModalOpened, setDeleteConfirmModalOpened] = useState(false);

    const form = useForm({
        initialValues: {
            id: "",
            email: "",
            org_code: "",
            roles: [] as string[],
            tags: [] as string[],
        },
    });

    const handleOpenModal = (user: User) => {
        if (isLoadingRoles) {
            notifications.show({
                title: "Loading",
                message: "Please wait while roles are being loaded...",
                loading: true,
            });
            return;
        }

        if (stableRoles.length === 0) {
            fetchRoles();
        }

        const currentUser = fetchedUsers.find((u: User) => u.id === user.id) || user;

        setSelectedUser(currentUser);
        form.setValues({
            id: currentUser.id,
            email: currentUser.email,
            roles: currentUser.roles,
            tags: currentUser.tags ?? [],
        });
        setModalOpened(true);
    };

    const handleSubmit = async (values: typeof form.values) => {
        setIsSubmitting(true);
        try {
            if (!selectedUser) {
                throw new Error("No user selected");
            }

            const updatedUser = await updateUser({
                data: {
                    id: selectedUser.id,
                    email: selectedUser.email,
                    org_code: selectedUser.org_code,
                    roles: [...new Set([...values.roles])],
                    tags: values.tags,
                },
            });

            setSelectedUser(updatedUser);

            const userIndex = fetchedUsers.findIndex((u: User) => u.id === updatedUser.id);
            if (userIndex !== -1) {
                const updatedUsers = [...fetchedUsers];
                updatedUsers[userIndex] = updatedUser;
            }

            notifications.show({
                title: "Success",
                message: "User updated successfully",
                color: "green",
            });

            setModalOpened(false);
        } catch (error) {
            notifications.show({
                title: "Error",
                message: error instanceof Error ? error.message : "Failed to update user",
                color: "red",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!selectedUser) return;

        setIsDeleting(true);
        try {
            await deleteUser({ id: selectedUser.id });

            notifications.show({
                title: "Success",
                message: "User deleted successfully",
                color: "green",
            });
            refetch();
            setDeleteConfirmModalOpened(false);
            setModalOpened(false);
        } catch (error) {
            notifications.show({
                title: "Error",
                message: error instanceof Error ? error.message : "Failed to delete user",
                color: "red",
            });
        } finally {
            setIsDeleting(false);
        }
    };

    // Create User Modal State
    const [createUserModalOpened, setCreateUserModalOpened] = useState(false);
    const [isCreatingUser, setIsCreatingUser] = useState(false);

    const createUserForm = useForm({
        initialValues: {
            email: "",
            roles: [] as string[],
            tags: [] as string[],
        },
        validate: {
            email: (value) =>
                !value ? "Email is required" : !value.includes("@") ? "Invalid email format" : null,
            roles: (value) => (value.length === 0 ? "At least one role is required" : null),
        },
    });

    const handleCreateUser = async (values: typeof createUserForm.values) => {
        setIsCreatingUser(true);
        try {
            const _createdUser = await createUser({
                email: values.email,
                roles: values.roles,
                tags: values.tags,
            });

            notifications.show({
                title: "Success",
                message: "User created successfully",
                color: "green",
            });

            setCreateUserModalOpened(false);
            createUserForm.reset();
            refetch();
        } catch (error) {
            notifications.show({
                title: "Error",
                message: error instanceof Error ? error.message : "Failed to create user",
                color: "red",
            });
        } finally {
            setIsCreatingUser(false);
        }
    };

    const [createRoleModalOpened, setCreateRoleModalOpened] = useState(false);
    const [isCreatingRole, setIsCreatingRole] = useState(false);

    const createRoleForm = useForm({
        initialValues: {
            id: "",
            description: "",
            datasources: [] as { id: string }[],
        },
        validate: {
            id: (value) => (!value ? "Role ID is required" : null),
            description: (value) => (!value ? "Description is required" : null),
        },
    });

    const handleCreateRole = async (values: typeof createRoleForm.values) => {
        setIsCreatingRole(true);
        try {
            const response = await postAPI({
                url: `${BACKEND_URL}/v1/auth/roles`,
                payload: values,
                instance,
            });

            if (!response?.ok) {
                throw new Error("Failed to create role");
            }

            notifications.show({
                title: "Success",
                message: "Role created successfully",
                color: "green",
            });
            // Refetch roles to get the newly created role
            fetchRoles();
            setCreateRoleModalOpened(false);
            createRoleForm.reset();
        } catch (error) {
            notifications.show({
                title: "Error",
                message: error instanceof Error ? error.message : "Failed to create role",
                color: "red",
            });
        } finally {
            setIsCreatingRole(false);
        }
    };

    const [roleSearchTerm, setRoleSearchTerm] = useState("");

    const table = useMantineReactTable<User>({
        layoutMode: "grid",
        columns,
        data: fetchedUsers ?? [],
        enableColumnOrdering: true,
        enablePinning: true,
        enableBottomToolbar: false,
        renderToolbarAlertBannerContent: () => <></>,
        renderTopToolbarCustomActions: () => (
            <Flex align="center" gap="md">
                <Text size="sm" c="dimmed">
                    {`Total: ${fetchedUsers.length.toLocaleString()}, (Showing ${table.getFilteredRowModel().rows.length.toLocaleString()})`}
                </Text>
            </Flex>
        ),
        enableColumnResizing: true,
        enableFilters: true,
        enablePagination: false,
        enableRowNumbers: true,
        enableClickToCopy: false,
        getRowId: (row: User) => row.id,
        mantineToolbarAlertBannerProps: {
            style: {
                display: "none",
                background: "#f0f0f0",
                fontWeight: "bold",
            },
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        enableSelectAll: false,
        selectAllMode: "page",
        enableGrouping: true,
        enableDensityToggle: false,
        mantineTableContainerProps: {
            ref: tableContainerRef,
            style: { maxHeight: "calc(100vh - 280px)" },
            onScroll: (event) => fetchMoreOnBottomReached(event.currentTarget),
        },
        mantineTableBodyRowProps: ({ row }) => ({
            onClick: () => handleOpenModal(row.original),
            sx: { cursor: "pointer" },
        }),
        state: {
            isLoading: isLoading || isLoadingRoles,
            showProgressBars: isRefetching,
            columnFilters,
            globalFilter,
            sorting,
        },
        initialState: {
            showGlobalFilter: true,
            density: "xs",
            showColumnFilters: true,
            columnVisibility: { oid: false },
        },
    });

    return (
        <>
            <Flex px="xl" pb="xl" direction="column" gap="md">
                <Flex gap="md" align="center" justify="space-between">
                    <Title lh={2} c="purple.8" order={2} tt="uppercase">
                        Manage Users
                    </Title>
                    <Button onClick={() => setCreateUserModalOpened(true)} variant="filled">
                        Create New User
                    </Button>
                </Flex>
            </Flex>

            <MantineReactTable table={table} />

            {/* Create User Modal */}
            <Modal
                closeButtonProps={{ "aria-label": "Close modal" }}
                opened={createUserModalOpened}
                onClose={() => {
                    setCreateUserModalOpened(false);
                    createUserForm.reset();
                    setRoleSearchTerm("");
                }}
                title="Create New User"
                size="lg"
            >
                <Box component="form" onSubmit={createUserForm.onSubmit(handleCreateUser)}>
                    <Stack gap="md">
                        <TextInput
                            required
                            label="Email"
                            placeholder="user@example.com"
                            {...createUserForm.getInputProps("email")}
                        />

                        <TagsInput
                            label="Tags"
                            placeholder="Type a tag and press Enter"
                            {...createUserForm.getInputProps("tags")}
                        />

                        <RoleSelectionComponent
                            form={createUserForm}
                            roleSearchTerm={roleSearchTerm}
                            setRoleSearchTerm={setRoleSearchTerm}
                        />

                        <Button type="submit" loading={isCreatingUser} fullWidth>
                            Create User
                        </Button>
                    </Stack>
                </Box>
            </Modal>

            <Modal
                closeButtonProps={{ "aria-label": "Close modal" }}
                opened={createRoleModalOpened}
                onClose={() => {
                    setCreateRoleModalOpened(false);
                    createRoleForm.reset();
                }}
                title="Create New Role"
                size="md"
            >
                <Box component="form" onSubmit={createRoleForm.onSubmit(handleCreateRole)}>
                    <Stack gap="md">
                        <TextInput
                            required
                            label="Role ID"
                            placeholder="e.g., admin_role"
                            {...createRoleForm.getInputProps("id")}
                        />

                        <Textarea
                            required
                            label="Description"
                            placeholder="Describe the purpose of this role"
                            minRows={3}
                            {...createRoleForm.getInputProps("description")}
                        />

                        <Button type="submit" loading={isCreatingRole} fullWidth>
                            Create Role
                        </Button>
                    </Stack>
                </Box>
            </Modal>

            <Modal
                closeButtonProps={{ "aria-label": "Close modal" }}
                opened={modalOpened}
                onClose={() => setModalOpened(false)}
                title={`Edit User: ${selectedUser?.email || ""}`}
                size="lg"
                styles={{
                    body: {
                        minHeight: 500,
                    },
                }}
            >
                <form onSubmit={form.onSubmit(handleSubmit)}>
                    <Stack gap="md">
                        <CopyableField label="Email" value={selectedUser?.email || ""} />
                        <SimpleGrid cols={3}>
                            <CopyableField label="User ID" value={selectedUser?.id || ""} />
                            <CopyableField label="OID" value={selectedUser?.oid || ""} />
                            <CopyableField label="Org Code" value={selectedUser?.org_code || ""} />
                        </SimpleGrid>

                        <TagsInput
                            label="Tags"
                            placeholder="Type a tag and press Enter"
                            {...form.getInputProps("tags")}
                        />

                        <RoleSelectionComponent
                            form={form}
                            roleSearchTerm={roleSearchTerm}
                            setRoleSearchTerm={setRoleSearchTerm}
                        />

                        <Group justify="flex-end" mt="md">
                            <Button
                                type="button"
                                variant="outline"
                                styles={{ root: { color: "black" } }}
                                onClick={() => setModalOpened(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                variant="filled"
                                color="red"
                                styles={{ root: { color: "black" } }}
                                onClick={() => setDeleteConfirmModalOpened(true)}
                            >
                                Delete User
                            </Button>
                            <Button
                                type="submit"
                                color="green"
                                styles={{ root: { color: "black" } }}
                                loading={isSubmitting}
                            >
                                Save Changes
                            </Button>
                        </Group>
                    </Stack>
                </form>
            </Modal>

            <Modal
                closeButtonProps={{ "aria-label": "Close modal" }}
                opened={deleteConfirmModalOpened}
                onClose={() => setDeleteConfirmModalOpened(false)}
                title="Confirm User Deletion"
                size="sm"
            >
                <Stack gap="md">
                    <Text>
                        Are you sure you want to delete the user <b>{selectedUser?.email}</b>?
                    </Text>
                    <Group justify="flex-end">
                        <Button
                            variant="outline"
                            onClick={() => setDeleteConfirmModalOpened(false)}
                        >
                            Cancel
                        </Button>
                        <Button color="red" loading={isDeleting} onClick={handleDeleteUser}>
                            Delete
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
};

export default ManageUsers;
